import {
  SuiteErrorV1Schema,
  type SuiteErrorV1,
} from "@fulcrum5/suite-contracts";
import { type Static, type TSchema } from "typebox";
import { Value } from "typebox/value";

export interface PlatformApiOptions {
  /** Reads the current opaque CSRF proof owned by the Platform session. */
  readonly csrfToken: () => string | null | undefined;
  readonly fetchImpl?: typeof fetch;
  readonly onUnauthenticated?: () => void;
}

export interface PlatformApiRequest<Schema extends TSchema> {
  readonly path: string;
  readonly responseSchema: Schema;
  readonly method?: string;
  readonly body?: unknown;
  readonly headers?: HeadersInit;
  readonly signal?: AbortSignal;
}

export interface PlatformApiGetRequest<Schema extends TSchema> {
  readonly path: string;
  readonly responseSchema: Schema;
  readonly headers?: HeadersInit;
  readonly signal?: AbortSignal;
}

export interface PlatformApiPatchRequest<Schema extends TSchema> {
  readonly path: string;
  readonly responseSchema: Schema;
  readonly body: unknown;
  readonly headers?: HeadersInit;
  readonly ifMatch?: string;
  readonly signal?: AbortSignal;
}

export interface PlatformApiDeleteRequest<Schema extends TSchema> {
  readonly path: string;
  readonly responseSchema: Schema;
  readonly headers?: HeadersInit;
  readonly ifMatch?: string;
  readonly signal?: AbortSignal;
}

export interface PlatformApiBytesRequest<Schema extends TSchema> {
  readonly path: string;
  readonly responseSchema: Schema;
  readonly bytes: BodyInit;
  readonly contentType: string;
  readonly headers?: HeadersInit;
  readonly ifMatch?: string;
  readonly signal?: AbortSignal;
}

export interface PlatformApiResponse<Data> {
  readonly data: Data;
  readonly requestId: string | null;
}

/** A browser-safe representation of the Platform's validated error envelope. */
export class PlatformApiError extends Error {
  readonly status: number;
  readonly error: SuiteErrorV1;
  readonly code: string;
  readonly requestId: string;
  readonly retryable: boolean;

  constructor(status: number, error: SuiteErrorV1) {
    super(error.message);
    this.name = "PlatformApiError";
    this.status = status;
    this.error = error;
    this.code = error.code;
    this.requestId = error.requestId;
    this.retryable = error.retryable;
  }
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const CLIENT_REQUEST_ID = "platform_web_client";

function isMutation(method: string): boolean {
  return !SAFE_METHODS.has(method.toUpperCase());
}

function headersWithIfMatch(headers: HeadersInit | undefined, ifMatch: string | undefined): Headers {
  const next = new Headers(headers);
  if (ifMatch === undefined) return next;
  next.set("if-match", ifMatch);
  return next;
}

function clientError(
  status: number,
  requestId: string | null,
  code: "network_unavailable" | "response_invalid",
  message: string,
  retryable: boolean,
): PlatformApiError {
  return new PlatformApiError(status, {
    code,
    message,
    requestId: requestId ?? CLIENT_REQUEST_ID,
    retryable,
  });
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * Typed boundary for Platform browser routes. The gateway remains the only
 * authority for session and CSRF validity; this client only transports the
 * current proof and validates the resulting contracts.
 */
export class PlatformApi {
  readonly #csrfToken: PlatformApiOptions["csrfToken"];
  readonly #fetch: typeof fetch;
  readonly #onUnauthenticated: (() => void) | undefined;
  #hasNotifiedUnauthenticated = false;

  constructor({ csrfToken, fetchImpl = globalThis.fetch.bind(globalThis), onUnauthenticated }: PlatformApiOptions) {
    this.#csrfToken = csrfToken;
    this.#fetch = fetchImpl;
    this.#onUnauthenticated = onUnauthenticated;
  }

  get<Schema extends TSchema>(
    input: PlatformApiGetRequest<Schema>,
  ): Promise<PlatformApiResponse<Static<Schema>>> {
    return this.request({ ...input, method: "GET" });
  }

  patch<Schema extends TSchema>(
    input: PlatformApiPatchRequest<Schema>,
  ): Promise<PlatformApiResponse<Static<Schema>>> {
    return this.request({
      path: input.path,
      responseSchema: input.responseSchema,
      method: "PATCH",
      body: input.body,
      headers: headersWithIfMatch(input.headers, input.ifMatch),
      signal: input.signal,
    });
  }

  delete<Schema extends TSchema>(
    input: PlatformApiDeleteRequest<Schema>,
  ): Promise<PlatformApiResponse<Static<Schema>>> {
    return this.#execute({
      path: input.path,
      responseSchema: input.responseSchema,
      method: "DELETE",
      headers: headersWithIfMatch(input.headers, input.ifMatch),
      signal: input.signal,
    });
  }

  putBytes<Schema extends TSchema>(
    input: PlatformApiBytesRequest<Schema>,
  ): Promise<PlatformApiResponse<Static<Schema>>> {
    const headers = headersWithIfMatch(input.headers, input.ifMatch);
    headers.set("content-type", input.contentType);
    return this.#execute({
      path: input.path,
      responseSchema: input.responseSchema,
      method: "PUT",
      headers,
      body: input.bytes,
      signal: input.signal,
    });
  }

  async request<Schema extends TSchema>(
    input: PlatformApiRequest<Schema>,
  ): Promise<PlatformApiResponse<Static<Schema>>> {
    const method = (input.method ?? "GET").toUpperCase();
    const headers = new Headers(input.headers);

    if (input.body !== undefined) {
      headers.set("content-type", "application/json");
    }
    return this.#execute({
      path: input.path,
      responseSchema: input.responseSchema,
      method,
      headers,
      ...(input.body === undefined ? {} : { body: JSON.stringify(input.body) }),
      signal: input.signal,
    });
  }

  async #execute<Schema extends TSchema>(
    input: {
      readonly path: string;
      readonly responseSchema: Schema;
      readonly method: string;
      readonly headers?: HeadersInit;
      readonly body?: BodyInit;
      readonly signal?: AbortSignal;
    },
  ): Promise<PlatformApiResponse<Static<Schema>>> {
    const method = input.method.toUpperCase();
    const headers = new Headers(input.headers);

    if (isMutation(method)) {
      const csrfToken = this.#csrfToken();
      if (csrfToken) {
        // The session-owned token takes precedence over caller supplied headers.
        headers.set("x-csrf-token", csrfToken);
      } else {
        headers.delete("x-csrf-token");
      }
    }

    let response: Response;
    try {
      response = await this.#fetch(input.path, {
        method,
        credentials: "same-origin",
        headers,
        ...(input.body === undefined ? {} : { body: input.body }),
        signal: input.signal,
      });
    } catch {
      throw clientError(
        0,
        null,
        "network_unavailable",
        "Platform could not be reached",
        true,
      );
    }

    const requestId = response.headers.get("x-request-id");
    if (response.status === 401) this.#notifyUnauthenticated();

    let payload: unknown;
    try {
      payload = await readJson(response);
    } catch {
      throw clientError(
        response.status,
        requestId,
        "response_invalid",
        "Platform returned an unreadable response",
        response.status >= 500,
      );
    }

    if (!response.ok) {
      if (Value.Check(SuiteErrorV1Schema, payload)) {
        throw new PlatformApiError(response.status, payload);
      }
      throw clientError(
        response.status,
        requestId,
        "response_invalid",
        "Platform returned an invalid error response",
        response.status >= 500,
      );
    }

    if (!Value.Check(input.responseSchema, payload)) {
      throw clientError(
        response.status,
        requestId,
        "response_invalid",
        "Platform returned an invalid response",
        true,
      );
    }

    return { data: payload, requestId };
  }

  #notifyUnauthenticated(): void {
    if (this.#hasNotifiedUnauthenticated) return;
    this.#hasNotifiedUnauthenticated = true;
    try {
      this.#onUnauthenticated?.();
    } catch {
      // The validated API error remains the caller's control-flow signal.
    }
  }
}
